import Vue from 'vue'
import formatName from 'filters/format-name'
import { addHighlightClass, truncateText } from 'util/text-utils'
import { ReviewProfileAttributes } from 'services/api-service'
import GridLayout from 'layouts/grid/grid-layout'
import MediaThumb from 'objects/media/media-thumb'
import PreviousRatings from 'components/review/previous-ratings'
import ProductAbout from 'components/product-about/product-about'
import ReviewByline from 'objects/byline/review-byline'
import ReviewDimension from 'components/review/dimension'
import ReviewResponses from 'components/review/review-responses'
import RatingStars from 'components/rating/rating-stars'
import ReviewToolbar from 'components/review/review-toolbar'
import { TYPE_PHOTO } from 'util/media-utils'

import withRender from './review.html'
import './review.scss'

interface IProfileAttribute {
  label: string
  value: string
}

export default withRender((Vue as any).extend({
  data() {
    return {
      // TODO: not sure how this is determined yet. May not be a data value
      areProfileAttributesStacked: false,
      // Just a single index (0) for review.text for now.  If ans when review updates are added,
      // a review may have more.
      // The index will be in the this list if the user clicked on the "Read More" for the text.
      expandedTextIndexes: [],
      showDetailsClicked: false
    }
  },
  props: {
    allItemsInLayout: { type: Array },
    currentLayout: { type: Object },
    isRelatedReview: { type: Boolean, default: false },
    mediaSize: { type: Number, default: 60 },
    review: { type: Object },
    showPurchaserBadge: Boolean,
    slotNamesAfterShowDetails: { type: Array },
    truncateAllowance: { type: Number, default: 99 },
    truncateLength: { type: Number, default: 500 }
  },
  components: {
    'tt-grid-layout': GridLayout,
    'tt-media-thumb': MediaThumb,
    'tt-previous-ratings': PreviousRatings,
    'tt-product-about': ProductAbout,
    'tt-review-byline': ReviewByline,
    'tt-dimension': ReviewDimension,
    'tt-review-responses': ReviewResponses,
    'tt-rating-stars': RatingStars,
    'tt-review-toolbar': ReviewToolbar
  },
  computed: {
    displayShowDetails() {
      return !this.showDetailsClicked && ~this.allItemsInLayout.indexOf('showDetails') &&
        this.hiddenSlotNames.length > 0
    },
    hasUpdates() {
      return this.review.updates && this.review.updates.length > 0
    },
    // in mobile layout, if 'showDetails' item is added, everything below it is initially hidden
    // from view.  When 'showDetails' is clicked, the items below it are shown
    hiddenSlotNames() {
      if (!this.showDetailsClicked) {
        return this.slotNamesAfterShowDetails.filter((slotName) => {
          // we only want to include items that have data to be presented.  For example,
          // customDim-5 should not be included if there are only 4 custom dims.
          if (slotName.indexOf('customDim') === 0) {
            const dimNum = parseInt(slotName.split('-')[1], 10)
            return this.review.dimensions.length >= dimNum
          } else if (this.slotHasData.hasOwnProperty(slotName)) {
            return this.slotHasData[slotName]
          }
          // all other slots will always have data for display
          return true
        })
      }
      return []
    },
    previousRatings() {
      if (this.hasUpdates) {
        // only want the udpates that have a rating and they need to be in order of most recent
        // which is the reverse order in which review.updates are listed
        return this.review.updates.filter(update => update.rating).reverse()
      }
      return []
    },
    productAttributes() {
      // TODO: how these attributes display is still being spec'd. This is just a guess on how
      // it may work
      if (this.review.catItem.attributes) {
        return this.review.catItem.attributes.map((attribute) => {
          return `${attribute.label}: ${attribute.value}`
        }).join(', ')
      }
      return ''
    },
    profileAttributes() {
      const attributesForDisplay = [] as Array<IProfileAttribute>
      const attributes = this.review.profileAttributes as ReviewProfileAttributes
      if (!attributes) {
        return attributesForDisplay
      }
      if (attributes.ageRange) {
        const label = this.$msg('review.attribute.age')
        attributesForDisplay.push(
          { label, value: attributes.ageRange }
        )
      }
      if (attributes.location) {
        const label = this.$msg('review.attribute.location')
        attributesForDisplay.push(
          { label, value: attributes.location }
        )
      }
      if (attributes.custom) {
        attributes.custom.forEach((item, i) => {
          attributesForDisplay.push(
            { label: item.label, value: item.value }
          )
        })
      }
      return attributesForDisplay
    },
    recommendIcon() {
      return this.review.recommend ? 'check' : 'close'
    },
    recommendText() {
      return this.review.recommend === undefined ? '' :
        this.$msg(this.review.recommend ? 'review.recommend' : 'review.notRecommend')
    },
    reviewDate() {
      if (this.hasUpdates) {
        return this.review.updates[this.review.updates.length - 1].updateDateFormatted
      }
      return this.review.dateCreatedFormatted
    },
    reviewTexts() {
      const reviewTexts = []
      if (this.review.text) {
        const truncated = this.expandedTextIndexes.indexOf(0) > -1 ? null :
          truncateText(this.review.text, this.truncateLength, this.truncateAllowance)
        reviewTexts.push({
          text: addHighlightClass(truncated || this.review.text),
          updateDateFormatted: this.review.dateCreatedFormatted,
          isTruncated: truncated
        })
      }
      for (let i = 0; i < this.textUpdates.length; i++) {
        const textUpdate = this.textUpdates[i]
        const truncatedUpdate = this.expandedTextIndexes.indexOf((i + 1)) > -1 ? null :
          truncateText(textUpdate.text, this.truncateLength, this.truncateAllowance)
        reviewTexts.push({
          text: addHighlightClass(truncatedUpdate || textUpdate.text),
          updateDateFormatted: textUpdate.dateCreatedFormatted,
          isTruncated: truncatedUpdate
        })
      }
      return reviewTexts
    },
    slotHasData() {
      return {
        relatedProduct: this.isRelatedReview,
        reviewTitle: this.review.title,
        productAttributes: this.productAttributes,
        recommend: this.recommendText,
        previousRatings: this.previousRatings.length > 0,
        media: this.review.media && this.review.media.length > 0,
        reviewText: this.reviewTexts.length > 0,
        profileAttributes: this.profileAttributes.length > 0,
        reviewResponses: this.review.responsesBySource.length > 0,
        purchasedOn: this.review.purchaseDateFormatted,
        syndication: this.syndicationAttribution,
        incentivized: this.review.incentivized
      }
    },
    syndicationAttribution() {
      const syndication = this.review.syndication
      if (syndication) {
        let source
        if (syndication.logoUrl) {
          source = `<img src="${syndication.logoUrl}" alt="${syndication.sourceName}" ` +
            'class="tt-c-review__syndication-logo">'
        } else {
          source = syndication.sourceName
        }
        if (syndication.siteUrl) {
          source = `<a href="${syndication.siteUrl}" class="tt-c-review__syndication-link" ` +
            `target="_blank" rel="nofollow">${source}</a>`
        }
        return this.$msg('review.syndicationAttribution', [source])
      }
      return ''
    },
    textUpdates() {
      if (this.hasUpdates) {
        return this.review.updates.filter(update => update.text !== '' && update.text !== undefined)
      }
      return []
    },
    title() {
      return addHighlightClass(this.review.title)
    },
    updateButtonId() {
      return `tt-review-update-${this.review.id}`
    },
    updateReviewText() {
      const text = this.$msg('review.updateReview')
      return text.replace('<button>',
        `<button id="${this.updateButtonId}" type="button" class="tt-o-button tt-o-button--flat">`)
    }
  },
  methods: {
    expandText(index) {
      this.expandedTextIndexes.push(index)
    },
    isSlotHidden(slotName) {
      return ~this.hiddenSlotNames.indexOf(slotName)
    },
    mediaLinkTitle(mediaItem) {
      const username = formatName(mediaItem.user)
      const type = mediaItem.type === TYPE_PHOTO ? 'photo' : 'video'
      return this.$msg(`review.opensMediaModal.${type}`, [username])
    },
    openMediaModal(mediaIndex) {
      this.review.media.forEach((mediaItem) => {
        // media do not have catItem populated.  Need to make sure they do before opening in modal
        if (!mediaItem.catItem) {
          mediaItem.catItem = this.review.catItem
        }
        // also need to populate basic review data for the modal
        if (!mediaItem.review) {
          mediaItem.review = {
            rating: this.review.rating,
            text: this.review.text,
            title: this.review.title
          }
        }
      })
      this.$emit('openMediaModal', this.review.media, mediaIndex)
    },
    onShowDetailsClick() {
      this.showDetailsClicked = true
    }
  },
  watch: {
    currentLayout(newLayout, oldLayout) {
      // something is causing this to be called when the layout hasn't changed
      // so checking just in case
      if (newLayout !== oldLayout) {
        this.showDetails = false
        this.expandedTextIndexes = []
      }
    }
  }
}))



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--1!(webpack)-strip-block!./src/components/review/review.ts